import {useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Popover, PopoverContent, PopoverTrigger} from '@/components/ui/popover'
import {normalize} from '@/lib/utils'

export function FilterMultiSelect({
  label,
  testId,
  options,
  selected,
  onChange,
}: {
  label: string
  testId: string
  options: {id: string; name: string}[]
  selected: Set<string>
  onChange: (next: Set<string>) => void
}) {
  const {t} = useTranslation()
  const [query, setQuery] = useState('')

  const matches = useMemo(() => {
    const needle = normalize(query)
    if (!needle) return options
    return options.filter((o) => normalize(o.name).includes(needle))
  }, [options, query])

  function toggle(id: string) {
    const next = new Set(selected)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    onChange(next)
  }

  return (
    <Popover>
      <PopoverTrigger
        type="button"
        data-testid={`${testId}-trigger`}
        aria-pressed={selected.size > 0}
        className={`rounded-full border px-2.5 py-0.5 text-xs ${selected.size > 0 ? 'border-primary bg-accent text-accent-foreground' : 'border-input text-muted-foreground'}`}>
        {label}
        {selected.size > 0 && <span className="ml-1 font-mono">({selected.size})</span>}
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2" data-testid={testId}>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('common.search')}
          data-testid={`${testId}-search`}
          className="mb-2 w-full rounded-lg border border-input bg-transparent px-2.5 py-1.5 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
        />
        <ul className="max-h-56 space-y-0.5 overflow-y-auto">
          {matches.length === 0 && (
            <li className="px-2 py-1.5 text-xs text-muted-foreground">{t('common.noResults')}</li>
          )}
          {matches.map((option) => (
            <li key={option.id}>
              {/* El testid va en el label: `filter({ hasText })` de Playwright no encuentra un <input>. */}
              <label
                data-testid={`${testId}-option`}
                className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-foreground hover:bg-secondary">
                <input type="checkbox" checked={selected.has(option.id)} onChange={() => toggle(option.id)} />
                <span className="min-w-0 truncate">{option.name}</span>
              </label>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  )
}
